/**
 * PreK Practice Constants
 * Centralized constants for all PreK practice screens
 */

import { TRACK_RANGES } from '../../../types/constants';

// ============================================================================
// Track IDs (same as assessment)
// ============================================================================
export type PreKPracticeTrackId =
  | 'TRACK_PREK_NAMING'
  | 'TRACK_PREK_SUBITIZING'
  | 'TRACK_PREK_COUNTING'
  | 'TRACK_PREK_WHATCOMESNEXT';

export const PREK_PRACTICE_TRACKS = {
  NAMING: 'TRACK_PREK_NAMING' as const,
  SUBITIZING: 'TRACK_PREK_SUBITIZING' as const,
  COUNTING: 'TRACK_PREK_COUNTING' as const,
  WHATCOMESNEXT: 'TRACK_PREK_WHATCOMESNEXT' as const,
};

// ============================================================================
// Practice Phase Types
// ============================================================================
export type PracticePhase = 'iDo' | 'weDo' | 'youDo' | 'accuracy' | 'fluency15' | 'fluency';

export const PRACTICE_PHASES = {
  I_DO: 'iDo' as const,
  WE_DO: 'weDo' as const,
  YOU_DO: 'youDo' as const,
  ACCURACY: 'accuracy' as const,
  FLUENCY_15: 'fluency15' as const,  // Used by K practice only
  FLUENCY: 'fluency' as const,       // Used by K practice only
};

// ============================================================================
// Practice Badge Configuration
// ============================================================================
export const PRACTICE_BADGE_CONFIG = {
  [PRACTICE_PHASES.I_DO]: {
    emoji: '👨‍🏫',
    label: 'I Do Practice',
    bgColor: 'bg-blue-400',
    textColor: 'text-white',
  },
  [PRACTICE_PHASES.WE_DO]: {
    emoji: '🤝',
    label: 'We Do Practice',
    bgColor: 'bg-green-400',
    textColor: 'text-white',
  },
  [PRACTICE_PHASES.YOU_DO]: {
    emoji: '🏆',
    label: 'You Do Practice',
    bgColor: 'bg-purple-400',
    textColor: 'text-white',
  },
  [PRACTICE_PHASES.ACCURACY]: {
    emoji: '🎯',
    label: 'Accuracy Practice',
    bgColor: 'bg-yellow-400',
    textColor: 'text-yellow-900',
  },
  [PRACTICE_PHASES.FLUENCY_15]: {  // Used by K practice only
    emoji: '⏱️',
    label: 'Fluency 15s',
    bgColor: 'bg-orange-400',
    textColor: 'text-white',
  },
  [PRACTICE_PHASES.FLUENCY]: {  // Used by K practice only
    emoji: '⏰',
    label: 'Fluency Practice',
    bgColor: 'bg-red-400',
    textColor: 'text-white',
  },
} as const;

// ============================================================================
// Timer Constants
// ============================================================================
export const PRACTICE_TIMER_CONSTANTS = {
  // Auto-advance delay after TTS completes (generic fallback)
  AUTO_ADVANCE_DELAY_MS: 2000,

  // Operation-specific auto-advance delays for IDo screens
  NUMBER_NAMING_AUTO_ADVANCE_MS: 3000,
  SUBITIZING_AUTO_ADVANCE_MS: 2000,
  COUNTING_AUTO_ADVANCE_MS: 2000,
  WHATCOMESNEXT_AUTO_ADVANCE_MS: 3000,

  // Delay before showing correct answer feedback
  CORRECT_ANSWER_DELAY_MS: 300,

  // Wrong answer flash duration
  WRONG_ANSWER_FLASH_MS: 1000,

  // Wrong answer retry delay
  WRONG_ANSWER_RETRY_MS: 1500,

  // We-Do correct answer highlight duration
  WE_DO_HIGHLIGHT_MS: 2000,

  // Fluency timer limits (used by K practice only)
  FLUENCY_TIMER_MS: 6000,
  FLUENCY_15_TIMER_MS: 15000,

  // Subitizing flash duration (1 second for PreK - longer for young children)
  SUBITIZING_FLASH_MS: 1000,

  // Tick interval
  TICK_INTERVAL_MS: 100,
} as const;

// ============================================================================
// Pass/Fail Criteria Constants
// ============================================================================
export const PRACTICE_CRITERIA = {
  // Accuracy thresholds
  PASS_ACCURACY_THRESHOLD: 0.9,          // 90% to pass
  FAIL_ACCURACY_THRESHOLD: 0.7,          // Below 70% to fail (accuracy practice)
  FAIL_ACCURACY_THRESHOLD_FLUENCY15: 0.7, // Below 70% to fail (fluency15 - used by K practice only)

  // Attempt windows
  ATTEMPT_WINDOW: 10,                    // Last 10 attempts for pass/fail checks

  // Engagement thresholds
  ENGAGEMENT_WINDOW_MS: 120000,          // 2 minutes
  MIN_ENGAGEMENT_ATTEMPTS: 8,            // 8+ answers in last 2 min to pass

  // Timeout tracking (used by K practice only)
  MAX_TIMEOUTS_FOR_FAIL: 3,              // 3+ timeouts in last 10 → fail (fluency15)

  // Soft fail for PreK attention span (accuracy practice)
  MAX_ATTEMPTS_BEFORE_SOFT_FAIL: 25,     // Soft fail after 25 attempts without passing
} as const;

// ============================================================================
// Fact ID Mappings for Practice
// ============================================================================

/**
 * Get the starting fact ID for a track (same as assessment)
 */
export const getTrackFactStart = (trackId: PreKPracticeTrackId): number => {
  const range = TRACK_RANGES[trackId];
  return range ? range[0] : 0;
};

/**
 * Convert a fact ID to a value (digit/quantity) for a given track
 */
export const factIdToValue = (trackId: PreKPracticeTrackId, factId: string): number => {
  const factNumber = parseInt(factId.replace('FACT', ''));

  switch (trackId) {
    case PREK_PRACTICE_TRACKS.NAMING:
      // FACT1574 = 0, FACT1575 = 1, ..., FACT1583 = 9
      return factNumber - 1574;

    case PREK_PRACTICE_TRACKS.SUBITIZING:
      // FACT1584 = 1, FACT1585 = 2, FACT1586 = 3, FACT1587 = 4
      return factNumber - 1583;

    case PREK_PRACTICE_TRACKS.COUNTING:
      // FACT1588 = 1, FACT1589 = 2, ..., FACT1592 = 5
      return factNumber - 1587;

    case PREK_PRACTICE_TRACKS.WHATCOMESNEXT:
      // FACT1595 = 3, FACT1596 = 4, ..., FACT1601 = 9
      return factNumber - 1592;

    default:
      return factNumber - getTrackFactStart(trackId);
  }
};

/**
 * Convert a value (digit/quantity) to a fact ID for a given track
 */
export const valueToFactId = (trackId: PreKPracticeTrackId, value: number): string => {
  switch (trackId) {
    case PREK_PRACTICE_TRACKS.NAMING:
      return `FACT${1574 + value}`;

    case PREK_PRACTICE_TRACKS.SUBITIZING:
      return `FACT${1583 + value}`;

    case PREK_PRACTICE_TRACKS.COUNTING:
      return `FACT${1587 + value}`;

    case PREK_PRACTICE_TRACKS.WHATCOMESNEXT:
      // Target 3 = FACT1595, etc.
      return `FACT${1592 + value}`;

    default:
      return `FACT${getTrackFactStart(trackId) + value}`;
  }
};

// ============================================================================
// Answer Choice Generation
// ============================================================================

/**
 * Generate answer choices for practice screens
 * Returns an array of 4 choices including the target
 */
export const generatePracticeAnswerChoices = (
  trackId: PreKPracticeTrackId,
  target: number
): number[] => {
  const choices = [target];
  const used = new Set([target]);

  // Determine valid range based on track
  let min = 0;
  let max = 9;

  switch (trackId) {
    case PREK_PRACTICE_TRACKS.NAMING:
      min = 0;
      max = 9;
      break;
    case PREK_PRACTICE_TRACKS.SUBITIZING:
      min = 1;
      max = 4;
      break;
    case PREK_PRACTICE_TRACKS.COUNTING:
      min = 1;
      max = 5;
      break;
    case PREK_PRACTICE_TRACKS.WHATCOMESNEXT:
      min = 0;
      max = 10;
      break;
  }

  // Fill remaining slots with random valid numbers
  while (choices.length < 4) {
    const candidate = Math.floor(Math.random() * (max - min + 1)) + min;
    if (!used.has(candidate)) {
      choices.push(candidate);
      used.add(candidate);
    }
  }

  // Shuffle the choices
  return choices.sort(() => Math.random() - 0.5);
};

// ============================================================================
// Visual Config (dot patterns, grids)
// ============================================================================
export const PRACTICE_VISUAL_CONFIG = {
  // 3x3 grid for subitizing
  SUBITIZING_GRID: [
    { x: 10, y: 10 }, { x: 50, y: 10 }, { x: 90, y: 10 },
    { x: 10, y: 50 }, { x: 50, y: 50 }, { x: 90, y: 50 },
    { x: 10, y: 90 }, { x: 50, y: 90 }, { x: 90, y: 90 },
  ],

  // 4x8 grid for counting
  COUNTING_GRID: [
    { x: 12.5, y: 6.25 }, { x: 37.5, y: 6.25 }, { x: 62.5, y: 6.25 }, { x: 87.5, y: 6.25 },
    { x: 12.5, y: 18.75 }, { x: 37.5, y: 18.75 }, { x: 62.5, y: 18.75 }, { x: 87.5, y: 18.75 },
    { x: 12.5, y: 31.25 }, { x: 37.5, y: 31.25 }, { x: 62.5, y: 31.25 }, { x: 87.5, y: 31.25 },
    { x: 12.5, y: 43.75 }, { x: 37.5, y: 43.75 }, { x: 62.5, y: 43.75 }, { x: 87.5, y: 43.75 },
    { x: 12.5, y: 56.25 }, { x: 37.5, y: 56.25 }, { x: 62.5, y: 56.25 }, { x: 87.5, y: 56.25 },
    { x: 12.5, y: 68.75 }, { x: 37.5, y: 68.75 }, { x: 62.5, y: 68.75 }, { x: 87.5, y: 68.75 },
    { x: 12.5, y: 81.25 }, { x: 37.5, y: 81.25 }, { x: 62.5, y: 81.25 }, { x: 87.5, y: 81.25 },
    { x: 12.5, y: 93.75 }, { x: 37.5, y: 93.75 }, { x: 62.5, y: 93.75 }, { x: 87.5, y: 93.75 },
  ],
} as const;
